import Header from "./Header";
import Commonplace from "./Commonplace";
import KnowledgeLists from "./KnowledgeLists";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import cycleImage from "../media/CommonSpace_Cycle.png";
import authImage from "../media/POD_Authentication.png";

import "../App.css";

import {
  Container,
  Typography,
  Link,
  Box,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";

import { useSession } from "@inrupt/solid-ui-react";

function Landing({ handleShowCommonplace, handleShowKnowledgeLists }) {
  return (
    <>
      <Header
        handleShowCommonplace={handleShowCommonplace}
        handleShowKnowledgeLists={handleShowKnowledgeLists}
        currentView="landing"
      />
      <Container sx={{ padding: (t) => t.spacing(5) }}>
        {/* Hero */}
        <Box
          sx={{
            textAlign: "center",
            py: (t) => t.spacing(4),
            px: (t) => t.spacing(2),
            maxWidth: "65ch",
            mx: "auto",
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            A shared space for quotes and ideas
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ mb: (t) => t.spacing(3) }}
          >
            Walk a road of quotes or explore a block world—all in 3D. No account required.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleShowCommonplace}
            sx={{ borderRadius: 2 }}
          >
            Explore CommonSpace
          </Button>
        </Box>

        <Accordion>
          <AccordionSummary
            className="how-it-works-container"
            expandIcon={<ExpandMoreIcon />}
            aria-controls="how-it-works-content"
            id="how-it-works-header"
          >
            <Typography variant="h4">How It Works</Typography>
          </AccordionSummary>
          <AccordionDetails
            className="walkthrough-container"
            sx={{ padding: (t) => t.spacing(5) }}
          >
            <Box sx={{ margin: (t) => t.spacing(2) }}>
              <Typography variant="h6">What is CommonSpace?</Typography>
              <Typography variant="body1" component="div">
                CommonSpace is a digital representation of a{" "}
                <Link
                  href="https://en.wikipedia.org/wiki/Commonplace_book"
                  target="_blank"
                >
                  commonplace book
                </Link>
                —a collection of knowledge. You can explore public quotes,
                connect your own data to personalize the experience, or
                contribute to the shared collection.
              </Typography>
            </Box>

            <Box sx={{ padding: (t) => t.spacing(2) }}>
              <Typography variant="h6">
                Visitors, Personalizers, Contributors
              </Typography>
              <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                With Solid, apps verify your identity with you. Instead of
                signing up on each app’s database, you grant access to your
                personal online datastore (POD)—your data stays under your
                control.
              </Typography>
              <Box component="ul" sx={{ pl: 2, "& li": { mb: 1 } }}>
                <li>
                  <strong>Visitor</strong> — Explore the shared collection
                  without logging in. Immerse yourself in publicly contributed
                  quotes.
                </li>
                <li>
                  <strong>Personalizer</strong> — Connect your POD (personal
                  online datastore) so your own quotes appear in the 3D scene
                  alongside the public list.
                </li>
                <li>
                  <strong>Contributor</strong> — A personalizer who also
                  contributes to the public CommonSpace datastore. You
                  authenticate with your WebID; you stay in control as the
                  self-sovereign owner of your data.
                </li>
              </Box>

              <img
                width={"500px"}
                src={cycleImage}
                loading="lazy"
                alt="commonspace-cycle"
              />

              <Typography variant="body1" component="div" sx={{ mt: 2 }}>
                In Solid-based apps like CommonSpace, you choose where your data
                lives (cloud, local device, or network) and which apps can
                access which parts. Granting access also authenticates you via
                your WebID—a two-way street: you trust the app with access, and
                the app trusts you in return.
              </Typography>

              <img
                width={"500px"}
                src={authImage}
                loading="lazy"
                alt="POD authentication"
                style={{ marginTop: 16 }}
              />

              <Typography variant="body1" component="div" sx={{ mt: 2 }}>
                Once you connect, you upgrade from visitor to personalizer (and
                optionally to contributor). You’re in control. This{" "}
                <Link
                  href="https://brittanydeventer.com/blog/commonspace-a-digital-commonplace-book/"
                  target="_blank"
                >
                  blog post
                </Link>{" "}
                by the creator explains more.
              </Typography>
            </Box>

            <Box sx={{ padding: (t) => t.spacing(2) }}>
              <Typography variant="h6">Educational Resources</Typography>
              <Typography variant="body1" component="div">
                CommonSpace is an educational resource for the Solid protocol
                and for visualizing control over your own dataset. Watch the
                walkthrough for an overview.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  target="_blank"
                  href="https://www.youtube.com/playlist?list=PLYQDrJ8RYKOGSyhHh4kr-EbJx0njxz_8P"
                >
                  Walkthrough Video
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="visit-content"
            id="visit-header"
          >
            <Typography variant="h4">Visit</Typography>
          </AccordionSummary>
          <AccordionDetails className="visit-container">
            <Box sx={{ margin: (t) => t.spacing(2) }}>
              <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                Immerse yourself in the shared public knowledge stored in the
                CommonSpace dataset.
              </Typography>
              <Box>
                <Button variant="outlined" onClick={handleShowCommonplace}>
                  Enter CommonSpace
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No need to log in or register.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: (t) => t.spacing(0.5) }}>
                  Move: left-click forward, right-click back. Look: drag the mouse. Switch between Road and Sky view on the Visit page.
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary
            className="personalize-container"
            expandIcon={<ExpandMoreIcon />}
            aria-controls="personalize-content"
            id="personalize-header"
            sx={{ justifyContent: "center" }}
          >
            <Typography variant="h4">Personalize</Typography>
          </AccordionSummary>
          <AccordionDetails
            className="personalize-container"
            justifyContent="center"
          >
            <Box sx={{ padding: (t) => t.spacing(2), maxWidth: "65ch" }}>
              <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                Connect your POD (personal online datastore) to create a private
                collection of quotes and personalize your CommonSpace experience.
              </Typography>
              <Box>
                <Button variant="outlined" onClick={handleShowKnowledgeLists}>
                  Connect
                </Button>
              </Box>
              <Typography variant="body1" component="div" sx={{ mt: 2 }}>
                After you connect, your personal quotes will be listed. You can
                add or remove quotes, then enter CommonSpace and see them in the
                scene.
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary
            className="contribute-container"
            expandIcon={<ExpandMoreIcon />}
            aria-controls="contribute-content"
            id="contribute-header"
          >
            <Typography variant="h4">Contribute</Typography>
          </AccordionSummary>
          <AccordionDetails className="contribute-container">
            <Box sx={{ padding: (t) => t.spacing(2), maxWidth: "65ch" }}>
              <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                Connect your POD and authenticate with your WebID to contribute
                to the public collection of knowledge.
              </Typography>
              <Box>
                <Button variant="outlined" onClick={handleShowKnowledgeLists}>
                  Connect
                </Button>
              </Box>
              <Typography variant="body1" component="div" sx={{ mt: 2 }}>
                Once you’re connected, you can add quotes to the public list and
                share with the community.
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Container>
    </>
  );
}

export default Landing;
